import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import {
  ControlValueAccessor,
  FormBuilder,
  FormControl,
  NG_VALUE_ACCESSOR
} from '@angular/forms';
import {MatSelectChange} from '@angular/material/select';
import {ReplaySubject} from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  takeUntil
} from 'rxjs/operators';

import {
  DobryAgentBlogArticle
} from '../../interface/dobry-agent-blog-article';

import {
  DobryAgentBlogService
} from '../../services/dobry-agent-blog.service';

import {
  CountedAndPaginated
} from '../../interface/response/counted-and-paginated';

@Component({
  selector: 'app-dict-dobry-agent-blog-articles',
  templateUrl: './dict-dobry-agent-blog-articles.component.html',
  styleUrls: ['./dict-dobry-agent-blog-articles.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(
        () => DictDobryAgentBlogArticlesComponent
      )
    }
  ]
})
export class DictDobryAgentBlogArticlesComponent
  implements OnInit, OnDestroy, ControlValueAccessor {

  @Input() label = 'Powiązane artykuły';
  @Input() multiple = true;
  @Input() control: FormControl = null;
  @Input() required = false;

  @Input() excludeUuid: string | null = null;

  @Output()
  selectedArticle = new EventEmitter<DobryAgentBlogArticle>();

  @Output()
  selectedArticles = new EventEmitter<DobryAgentBlogArticle[]>();

  items: DobryAgentBlogArticle[] = [];

  /**
   * Trzymamy osobno zaznaczone artykuły,
   * ponieważ mogą nie być obecne na aktualnej stronie wyników.
   */
  selectedItems: DobryAgentBlogArticle[] = [];

  filterControl: FormControl;

  loading = false;
  loadingMore = false;

  page = 0;
  readonly pageSize = 20;

  total = 0;
  search = '';

  externalControl = true;
  disabled = false;

  value: string | string[] | null = null;

  private destroyed$ = new ReplaySubject<boolean>(1);

  constructor(
    private blogService: DobryAgentBlogService,
    private fb: FormBuilder,
  ) {
    this.filterControl = this.fb.control('');
  }

  onChange: any = () => {};
  onTouched: any = () => {};

  ngOnInit(): void {
    if (this.control === null) {
      this.control = new FormControl(
        this.multiple ? [] : null
      );

      this.externalControl = false;
    }

    this.value = this.control.value;

    this.filterControl.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntil(this.destroyed$)
      )
      .subscribe((value: string | null) => {
        this.search = (value ?? '').trim();

        this.loadFirstPage();
      });

    this.control.valueChanges
      .pipe(
        takeUntil(this.destroyed$)
      )
      .subscribe((value: string | string[] | null) => {
        this.value = value;

        this.syncSelectedItems();
        this.ensureSelectedArticlesLoaded();
      });

    this.loadFirstPage();
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  writeValue(value: string | string[] | null): void {
    if (this.externalControl) {
      return;
    }

    this.value = value;

    this.control.setValue(
      value,
      {emitEvent: false}
    );
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;

    if (this.externalControl) {
      return;
    }

    if (isDisabled) {
      this.control.disable({emitEvent: false});
      return;
    }

    this.control.enable({emitEvent: false});
  }

  onSelectionChange(event: MatSelectChange): void {
    this.value = event.value;

    this.onChange(this.value);
    this.onTouched();

    this.syncSelectedItems();
    this.ensureSelectedArticlesLoaded();
  }

  removeArticle(
    article: DobryAgentBlogArticle,
    event?: MouseEvent
  ): void {
    event?.preventDefault();
    event?.stopPropagation();

    if (this.multiple) {
      const value = this.getSelectedUuids()
        .filter(uuid => uuid !== article.uuid);

      this.control.setValue(value);
      this.value = value;

      this.selectedItems = this.selectedItems.filter(
        item => item.uuid !== article.uuid
      );

      this.onChange(value);
      this.onTouched();

      this.selectedArticles.emit([
        ...this.selectedItems
      ]);

      return;
    }

    this.control.setValue(null);
    this.value = null;
    this.selectedItems = [];

    this.onChange(null);
    this.onTouched();
  }

  clearValue(): void {
    const value = this.multiple ? [] : null;

    this.control.setValue(value);
    this.value = value;

    this.selectedItems = [];

    this.onChange(value);
    this.onTouched();

    if (this.multiple) {
      this.selectedArticles.emit([]);
      return;
    }

    this.selectedArticle.emit(null);
  }

  loadMore(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();

    if (!this.hasMore || this.loadingMore) {
      return;
    }

    this.page++;

    this.loadPage(true);
  }

  get hasMore(): boolean {
    return this.items.length < this.total;
  }

  get hasSelectedArticles(): boolean {
    return this.selectedItems.length > 0;
  }

  private loadFirstPage(): void {
    this.page = 0;
    this.items = [];
    this.total = 0;

    this.loadPage(false);
  }

  private loadPage(append: boolean): void {
    if (append) {
      this.loadingMore = true;
    } else {
      this.loading = true;
    }

    const filters: Record<string, any> = {
      active: true,
    };

    if (this.search) {
      filters.context = this.search;
    }

    this.blogService
      .searchList(
        this.page,
        this.pageSize,
        filters,
        {
          sortBy: 'publicationDate',
          sortOrder: 'DESC'
        }
      )
      .pipe(
        takeUntil(this.destroyed$)
      )
      .subscribe({
        next: (
          response: CountedAndPaginated<DobryAgentBlogArticle>
        ) => {
          const newItems = this.excludeCurrentArticle(
            response.items
          );

          this.total = response.pagination.total;

          this.items = append
            ? this.mergeUnique(this.items, newItems)
            : newItems;

          this.loading = false;
          this.loadingMore = false;

          this.syncSelectedItems();
          this.ensureSelectedArticlesLoaded();
        },
        error: () => {
          this.loading = false;
          this.loadingMore = false;
        }
      });
  }

  private getSelectedUuids(): string[] {
    if (this.multiple) {
      return Array.isArray(this.control.value)
        ? this.control.value
        : [];
    }

    return this.control.value
      ? [this.control.value]
      : [];
  }

  private syncSelectedItems(): void {
    const selectedUuids = this.getSelectedUuids();

    if (!selectedUuids.length) {
      this.selectedItems = [];

      if (this.multiple) {
        this.selectedArticles.emit([]);
      }

      return;
    }

    const availableItems = this.mergeUnique(
      this.selectedItems,
      this.items
    );

    this.selectedItems = availableItems.filter(
      article => selectedUuids.includes(article.uuid)
    );

    if (this.multiple) {
      this.selectedArticles.emit([
        ...this.selectedItems
      ]);

      return;
    }

    const selected = this.selectedItems[0];

    if (selected) {
      this.selectedArticle.emit(selected);
    }
  }

  private ensureSelectedArticlesLoaded(): void {
    const selectedUuids = this.getSelectedUuids();

    if (!selectedUuids.length) {
      return;
    }

    const loadedUuids = new Set(
      this.mergeUnique(
        this.items,
        this.selectedItems
      ).map(article => article.uuid)
    );

    const missingUuids = selectedUuids.filter(
      uuid =>
        !loadedUuids.has(uuid) &&
        uuid !== this.excludeUuid
    );

    missingUuids.forEach(uuid => {
      this.blogService
        .get(uuid)
        .pipe(
          takeUntil(this.destroyed$)
        )
        .subscribe({
          next: article => {
            if (article.uuid === this.excludeUuid) {
              return;
            }

            this.selectedItems = this.mergeUnique(
              this.selectedItems,
              [article]
            );

            this.items = this.mergeUnique(
              this.items,
              [article]
            );

            this.syncSelectedItems();
          }
        });
    });
  }

  private excludeCurrentArticle(
    items: DobryAgentBlogArticle[]
  ): DobryAgentBlogArticle[] {
    if (!this.excludeUuid) {
      return items;
    }

    return items.filter(
      article => article.uuid !== this.excludeUuid
    );
  }

  private mergeUnique(
    current: DobryAgentBlogArticle[],
    next: DobryAgentBlogArticle[]
  ): DobryAgentBlogArticle[] {
    const map = new Map<string, DobryAgentBlogArticle>();

    [...current, ...next].forEach(article => {
      map.set(article.uuid, article);
    });

    return Array.from(map.values());
  }
}
